import React, { useMemo, type FC } from 'react';
import { styled } from '@compiled/react';
import { Box, Flex, Grid, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import AsyncIcon from '@atlassian/jira-common-components-async-icon';
import { useIntl } from '@atlassian/jira-intl';
import type { ChildIssueType } from '@atlassian/jira-issue-view-common-types/src/child-issue-type';
import { useIsCompactMode } from '@atlassian/jira-issue-view-compact-mode';
import messages from './messages';

type Props = {
	childIssueTypes: ChildIssueType[];
	onIssueTypeButtonClick(issueType: ChildIssueType): void;
};

export const IssueTypeOptions: FC<Props> = ({ childIssueTypes, onIssueTypeButtonClick }) => {
	const { formatMessage } = useIntl();
	const isCompact = useIsCompactMode();
	const renderIssueTypeOptions = useMemo(
		() =>
			childIssueTypes.map((childIssueType) => (
				<IssueTypeButton
					data-testid="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.common.issue-type-options.issue-type-button"
					key={childIssueType.name}
					onClick={() => onIssueTypeButtonClick(childIssueType)}
				>
					<Flex direction="row" columnGap="space.100">
						<AsyncIcon url={childIssueType.iconUrl} title={childIssueType.name} />{' '}
						{childIssueType.name}
					</Flex>
				</IssueTypeButton>
			)),
		[childIssueTypes, onIssueTypeButtonClick],
	);

	return (
		<Box testId="issue-view-common-views.child-issues-panel.ai-issue-breakdown.ui.common.issue-type-options">
			<IssueTypeOptionsHeader>
				{formatMessage(messages.issueTypeOptionsHeader)}
			</IssueTypeOptionsHeader>

			<Box xcss={issueTypeOptionsContainerStyles}>
				<Grid
					alignItems="center"
					rowGap="space.150"
					columnGap="space.150"
					xcss={isCompact ? compactGridOptions : responsiveGridOptions}
				>
					{renderIssueTypeOptions}
				</Grid>
			</Box>
		</Box>
	);
};

const issueTypeOptionsContainerStyles = xcss({
	padding: 'space.200',
});

const compactGridOptions = xcss({
	[media.above.xxs]: { gridTemplateColumns: 'repeat(1, 1fr)' },
});

const responsiveGridOptions = xcss({
	[media.above.xxs]: { gridTemplateColumns: 'repeat(1, 1fr)' },
	[media.above.md]: {
		gridTemplateColumns: 'repeat(2, 1fr)',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueTypeButton = styled.button({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	border: `1px solid ${token('color.background.accent.gray.subtler', colors.N30)}`,

	height: token('space.500', '40px'),
	borderRadius: token('space.050', '4px'),
	padding: `${token('space.100', '8px')} ${token('space.150', '8px')} ${token('space.100', '8px')} ${token('space.150', '8px')}`,
	background: 'none',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '14px',
	cursor: 'pointer',
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		background: token('color.background.neutral.subtle.hovered', colors.N30),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueTypeOptionsHeader = styled.div({
	color: token('color.text.subtlest', '#626F86'),
	fontWeight: token('font.weight.medium', '400'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '14px',
	marginTop: token('space.200', '16px'),
	marginLeft: token('space.200', '16px'),
});
